import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PageHeader from './PageHeader';
import {
  Sun, Moon, Coffee, Briefcase, Users, BookOpen, Leaf,
  Sparkle, Edit, ChevronRight, Plus, ArrowRight, Bolt, X,
} from './Icons';
import useHaptics from '../hooks/useHaptics';
import { apiGet, apiPost, apiPut, apiDelete } from '../lib/api';
import ActivityDetail from './ActivityDetail';
import LogTimeSheet from './LogTimeSheet';
import './Plans.css';

const ICONS = [
  [/(morning|ritual|sunrise|wake|meditat)/i, Sun],
  [/(lunch|coffee|reset|break|meal|eat|fork)/i, Coffee],
  [/(client|meet|call|partner|standup)/i, Users],
  [/(deep|focus|strategy|build|code|design|deck)/i, Briefcase],
  [/(learn|study|read|book|gtm|product)/i, BookOpen],
  [/(evening|night|reflect|review|journal)/i, Leaf],
  [/(sleep|moon|wind)/i, Moon],
];
const pickIcon = (n = '') => (ICONS.find(([re]) => re.test(n)) || [, Sparkle])[1];

const fmtMinutes = (m) => {
  if (m == null) return '';
  if (m < 60) return `${m} min`;
  const h = Math.floor(m / 60), r = m % 60;
  return r ? `${h}h ${r}m` : `${h}h`;
};
const pad = (n) => String(n).padStart(2, '0');
const isoDate = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const todayIso = () => isoDate(new Date());
const DOW = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

// Fix #6: parse YYYY-MM-DD as local midnight, not UTC
function parseIsoDateLocal(iso) {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(y, m - 1, d);
}

function buildWeek(selected) {
  const d = parseIsoDateLocal(selected);
  const dow = d.getDay();
  const monday = new Date(d);
  monday.setDate(d.getDate() + (dow === 0 ? -6 : 1 - dow));
  return Array.from({ length: 7 }, (_, i) => {
    const day = new Date(monday);
    day.setDate(monday.getDate() + i);
    return { dow: DOW[day.getDay()], num: day.getDate(), iso: isoDate(day) };
  });
}

function normalizePlan(activities, snapshot) {
  const items = Array.isArray(snapshot) ? snapshot
    : snapshot?.items || snapshot?.activities || [];
  if (!items.length) return [];
  const byId = new Map((activities || []).map((a) => [a.activity_id, a]));
  return items.map((p, i) => {
    const a = byId.get(p.activity_id) || {};
    const time = (p.preferred_time || a.preferred_time || '').slice(0, 5);
    const priorityNum = (p.priority != null ? p.priority : a.priority);
    return {
      id: p.activity_id || a.activity_id || `plan-${i}`,
      time,
      name: p.name || a.name || 'Untitled',
      desc: p.short_description || a.short_description || p.description || a.description || '',
      minutes: p.target_minutes || p.duration_minutes || a.duration_minutes || a.target_minutes || 30,
      priorityNum: typeof priorityNum === 'number' ? priorityNum : null,
      priority: !!p.is_top_priority,
    };
  });
}

export default function Plans({ userId, user }) {
  const haptics = useHaptics();
  const [selectedIso, setSelectedIso] = useState(todayIso());
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true); // Fix #11
  const [editingRow, setEditingRow] = useState(null);
  const [adding, setAdding] = useState(false);
  const [actionFor, setActionFor] = useState(null);
  const [detailFor, setDetailFor] = useState(null);
  const [completed, setCompleted] = useState(() => new Set());
  const [loggedTotals, setLoggedTotals] = useState(() => ({})); // { activityId: totalMinutesToday }
  const [logSheetFor, setLogSheetFor] = useState(null);
  const longPressTimer = useRef(null);
  // Fix #6: use parseIsoDateLocal instead of new Date(selectedIso)
  const week = useMemo(() => buildWeek(selectedIso), [selectedIso]);

  // Silent refetch of today's progress — mirrors Today.js so saved logs
  // from any view (Today, ActivityDetail, LogTimeSheet) update Plans'
  // logged totals + completion state without a manual refresh.
  const refetchTodayProgress = useCallback(async () => {
    if (!userId) return;
    if (selectedIso !== todayIso()) return; // only meaningful for today
    try {
      const r = await apiGet(`/api/progress/today/${userId}`);
      if (!r.ok) return;
      const data = await r.json().catch(() => null);
      if (!data) return;
      const ids = data.completed_activity_ids;
      setCompleted(Array.isArray(ids) ? new Set(ids) : new Set());
      const totals = {};
      if (Array.isArray(data.logs)) {
        data.logs.forEach((l) => {
          if (!l?.activity_id) return;
          const m = Number(l.actual_minutes) || 0;
          totals[l.activity_id] = (totals[l.activity_id] || 0) + m;
        });
      }
      setLoggedTotals(totals);
    } catch (_) { /* silent */ }
  }, [userId, selectedIso]);

  // Listen for hx:logs-changed (dispatched by LogTimeSheet etc.) so
  // Plans stays in sync with logs saved elsewhere in the app.
  useEffect(() => {
    if (!userId) return undefined;
    const onLogsChanged = () => refetchTodayProgress();
    window.addEventListener('hx:logs-changed', onLogsChanged);
    return () => window.removeEventListener('hx:logs-changed', onLogsChanged);
  }, [userId, refetchTodayProgress]);

  useEffect(() => {
    if (!userId) return;
    let cancelled = false;
    setLoading(true); // Fix #11
    (async () => {
      try {
        const isToday = selectedIso === todayIso();
        const requests = [
          apiGet(`/api/activities/${userId}`),
          apiGet(`/api/plan-snapshots/${userId}/${selectedIso}`).catch(() => null),
        ];
        // Fix #12: fetch today's progress when viewing today
        if (isToday) {
          requests.push(apiGet(`/api/progress/today/${userId}`).catch(() => null));
        }
        const settled = await Promise.allSettled(requests);
        const activities = settled[0].status === 'fulfilled' && settled[0].value?.ok
          ? await settled[0].value.json().catch(() => []) : [];
        const snapshot = settled[1].status === 'fulfilled' && settled[1].value?.ok
          ? await settled[1].value.json().catch(() => []) : [];
        // Fix #4: remove fallback that duplicates activities as plan
        const normalized = normalizePlan(activities, snapshot);
        if (cancelled) return;
        setRows(normalized);
        // Fix #12: pre-populate completed set from progress data
        if (isToday && settled[2]?.status === 'fulfilled' && settled[2].value?.ok) {
          const progress = await settled[2].value.json().catch(() => null);
          const ids = progress?.completed_activity_ids;
          setCompleted(Array.isArray(ids) ? new Set(ids) : new Set());
          // Build sum-of-minutes-per-activity from today's logs
          const totals = {};
          if (Array.isArray(progress?.logs)) {
            progress.logs.forEach((l) => {
              if (!l?.activity_id) return;
              const m = Number(l.actual_minutes) || 0;
              totals[l.activity_id] = (totals[l.activity_id] || 0) + m;
            });
          }
          setLoggedTotals(totals);
        } else {
          setCompleted(new Set());
          setLoggedTotals({});
        }
      } catch { if (!cancelled) setRows([]); }
      finally { if (!cancelled) setLoading(false); } // Fix #11
    })();
    return () => { cancelled = true; };
  }, [userId, selectedIso]);

  const timed = useMemo(
    () => rows.filter((r) => r.time).sort((a, b) => a.time.localeCompare(b.time)),
    [rows]
  );
  const untimed = useMemo(() => rows.filter((r) => !r.time), [rows]);

  const priorityIdx = useMemo(() => {
    if (!timed.length) return -1;
    const explicit = timed.findIndex((r) => r.priority);
    if (explicit >= 0) return explicit;
    const highest = timed.reduce((best, r, i) => {
      if (r.priorityNum == null) return best;
      if (best == null) return i;
      return (r.priorityNum > timed[best].priorityNum) ? i : best;
    }, null);
    if (highest != null && timed[highest].priorityNum >= 8) return highest;
    return -1;
  }, [timed]);

  const flashToast = (msg, kind = 'info') => {
    if (typeof window !== 'undefined' && typeof window.showHxToast === 'function') {
      window.showHxToast(msg, kind);
    }
  };
  const onPickDay = (iso) => { haptics.trigger('light'); setSelectedIso(iso); };
  const isToday = selectedIso === todayIso();

  const startLongPress = (row, idx) => {
    if (idx === priorityIdx) return;
    clearTimeout(longPressTimer.current);
    longPressTimer.current = setTimeout(() => {
      haptics.trigger('medium'); setActionFor(row);
    }, 500);
  };
  const cancelLongPress = () => clearTimeout(longPressTimer.current);

  // Fix #2: use /api/activities/${userId}/${t.id} (two segments)
  const handleEditSave = async (next) => {
    if (!editingRow) return;
    const { row: t } = editingRow;
    const updated = { ...t, name: next.name, minutes: Number(next.minutes) || t.minutes };
    setRows((rs) => rs.map((r) => (r.id === t.id ? updated : r)));
    setEditingRow(null);
    haptics.trigger('success');
    if (!t.id || /^(s\d|local-)/.test(String(t.id))) return;
    if (userId) {
      try {
        await apiPut(`/api/activities/${userId}/${t.id}`, { name: updated.name, target_minutes: updated.minutes });
      } catch {
        flashToast('Could not save — try again', 'error'); // Fix #13
      }
    }
  };

  // Fix #5: guard against double-submit with ref; AddFields handles in-flight state
  const submittingRef = useRef(false);
  const handleAddSave = async (entry) => {
    if (submittingRef.current) return;
    submittingRef.current = true;
    const localId = `local-${Date.now()}`;
    const newRow = {
      id: localId,
      time: entry.time || '',
      name: entry.name,
      minutes: Number(entry.minutes) || 30,
      desc: '',
    };
    setRows((rs) => [...rs, newRow]);
    setAdding(false);
    haptics.trigger('success');
    try {
      if (!userId) return;
      const res = await apiPost(`/api/activities/${userId}`, {
        name: newRow.name,
        preferred_time: newRow.time || undefined,
        target_minutes: newRow.minutes,
      });
      if (res.ok) {
        const created = await res.json().catch(() => null);
        const newActivityId = created?.activity_id;
        if (newActivityId) {
          setRows((rs) => rs.map((r) => (r.id === localId ? { ...r, id: newActivityId } : r)));
        }
      } else {
        flashToast('Created but not added to today\'s plan — try refreshing', 'error');
      }
    } catch {
      flashToast('Created but not added to today\'s plan — try refreshing', 'error');
    } finally {
      submittingRef.current = false;
    }
  };

  // Fix #3: use /api/activities/${userId}/${row.id} (two segments)
  const handleRemove = async (row) => {
    setRows((rs) => rs.filter((r) => r.id !== row.id));
    setActionFor(null);
    haptics.trigger('warning');
    if (!row.id || /^(s\d|local-)/.test(String(row.id))) return;
    if (userId) {
      try {
        await apiDelete(`/api/activities/${userId}/${row.id}`);
      } catch {
        flashToast('Could not delete — try again', 'error'); // Fix #13
      }
    }
  };

  // LogTimeSheet success handler — increments today's logged total for the row
  const handleLogged = (row, savedLog) => {
    if (!row || !savedLog) return;
    const m = Number(savedLog.actual_minutes) || 0;
    const planned = row.minutes || 0;
    setLoggedTotals((prev) => {
      const newTotal = (prev[row.id] || 0) + m;
      if (planned > 0 && newTotal >= planned) {
        setCompleted((c) => new Set([...c, row.id]));
      } else if (planned > 0 && newTotal < planned) {
        setCompleted((c) => { const n = new Set(c); n.delete(row.id); return n; });
      }
      return { ...prev, [row.id]: newTotal };
    });
    haptics.trigger('success');
  };

  const openLogSheet = (row) => {
    haptics.trigger('light');
    setLogSheetFor(row);
  };

  return (
    <div className="page plans-page">
      <PageHeader user={user} />
      <header className="plans-title-block">
        <h1 className="h-display">Plans</h1>
        <p className="subtitle">Your day, thoughtfully planned.</p>
      </header>

      <nav className="week-strip" aria-label="Week selector">
        <div className="week-strip-inner">
          {week.map((d) => {
            const active = d.iso === selectedIso;
            const isTodayCell = d.iso === todayIso();
            const isPast = d.iso < todayIso();
            return (
              <button key={d.iso} type="button"
                className={`day-cell${active ? ' is-active' : ''}${isTodayCell ? ' is-today' : ''}${isPast ? ' is-past' : ''}`}
                onClick={() => onPickDay(d.iso)}
                aria-pressed={active} aria-label={`${d.dow} ${d.num}`}>
                <span className="day-dow eyebrow">{d.dow}</span>
                <span className="day-num-wrap"><span className="day-num">{d.num}</span></span>
              </button>
            );
          })}
        </div>
        {!isToday && (
          <button type="button" className="plans-today-btn"
            onClick={() => { haptics.trigger('light'); setSelectedIso(todayIso()); }}>
            Today
          </button>
        )}
        <div className="rule plans-rule" aria-hidden />
      </nav>

      {/* Fix #11: show nothing during load; show empty state only when done and truly empty */}
      {!loading && rows.length === 0 && (
        <div className="plans-empty">
          <h2 className="h-2">A clear day.</h2>
          <p className="plans-empty-text">Add your first intention to shape it.</p>
        </div>
      )}

      {rows.length > 0 && (
        <>
          {timed.length > 0 && (
            <ol className="plans-timeline stagger" aria-label="Plan timeline">
              {timed.map((row, i) => {
                const isPriority = priorityIdx >= 0 && i === priorityIdx;
                const logged = loggedTotals[row.id] || 0;
                const planned = row.minutes || 0;
                const met = planned > 0 && logged >= planned;
                if (isPriority) {
                  return (
                    <li key={row.id} className="timeline-item priority-item">
                      <div className="priority-row">
                        <div className="priority-time mono">{row.time}</div>
                        {/* Fix #10: priority card is now tappable */}
                        <article className="black-card priority-card"
                          role="button" tabIndex={0}
                          onClick={() => { haptics.trigger('light'); setActionFor(row); }}
                          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setActionFor(row); } }}
                          aria-label={`${row.name} — top priority`}>
                          <button type="button" className="priority-edit"
                            onClick={(e) => { e.stopPropagation(); haptics.trigger('light'); setEditingRow({ row, idx: i }); }}
                            aria-label="Edit top priority">
                            <Edit size={14} color="var(--ink)" />
                          </button>
                          <div className="eyebrow-gold priority-eyebrow">
                            <span className="priority-dot" aria-hidden />TOP PRIORITY
                          </div>
                          <h2 className="h-2 priority-title">{row.name || 'Untitled activity'}</h2>
                          {row.desc && <p className="priority-desc">{row.desc}</p>}
                          <div className="priority-footer">
                            <span className="priority-meta mono">
                              <Bolt size={13} color="var(--gold-400)" />{fmtMinutes(row.minutes)}
                            </span>
                            <ArrowRight size={16} color="var(--gold-300)" />
                          </div>
                        </article>
                      </div>
                    </li>
                  );
                }
                return (
                  <li key={row.id} className={`timeline-item${met ? ' is-done' : ''}`}>
                    <div className={`timeline-row-wrap${met ? ' is-done' : ''}`}>
                      <button type="button" className={`timeline-row${met ? ' is-done' : ''}`}
                        onClick={() => { haptics.trigger('light'); setDetailFor(row); }}
                        onContextMenu={(e) => { e.preventDefault(); setActionFor(row); }}
                        onTouchStart={() => startLongPress(row, i)}
                        onTouchEnd={cancelLongPress} onTouchMove={cancelLongPress}
                        onMouseDown={() => startLongPress(row, i)}
                        onMouseUp={cancelLongPress} onMouseLeave={cancelLongPress}
                        aria-label={`${row.time} — ${row.name}`}>
                        <span className="row-time mono">{row.time}</span>
                        <span className="row-icon" aria-hidden>
                          {(() => { const RowIcon = pickIcon(row.name); return <RowIcon size={18} color="var(--gold-400)" />; })()}
                        </span>
                        <span className="row-content">
                          <span className="row-title">{row.name}</span>
                          {row.desc && <span className="row-desc">{row.desc}</span>}
                          {planned > 0 && logged > 0 && (
                            <span className="row-progress" aria-hidden>
                              <span
                                className={`row-progress-bar${met ? ' is-met' : ''}`}
                                style={{ width: `${Math.min(100, Math.round((logged / planned) * 100))}%` }}
                              />
                            </span>
                          )}
                        </span>
                        <span className={`row-mins mono${met ? ' is-met' : ''}`}>
                          {planned > 0 && logged > 0 ? `${logged}/${planned}m` : fmtMinutes(row.minutes)}
                        </span>
                        <span className="row-chevron" aria-hidden><ChevronRight size={16} color="currentColor" /></span>
                      </button>
                    </div>
                  </li>
                );
              })}
            </ol>
          )}

          {untimed.length > 0 && (
            <section className="plans-todo-section" aria-label="Tasks without a fixed time">
              <div className="eyebrow plans-todo-eyebrow">NO FIXED TIME</div>
              <ul className="plans-todo-list">
                {untimed.map((row) => {
                  const logged = loggedTotals[row.id] || 0;
                  const planned = row.minutes || 0;
                  const met = planned > 0 && logged >= planned;
                  const done = met || completed.has(row.id);
                  return (
                    <li key={row.id} className={`plans-todo-row${done ? ' is-done' : ''}`}>
                      <button type="button"
                        className="plans-todo-row-btn"
                        onClick={() => { haptics.trigger('light'); setDetailFor(row); }}
                        onContextMenu={(e) => { e.preventDefault(); setActionFor(row); }}
                        aria-label={`${row.name}${done ? ' (completed)' : ''}`}>
                        <span className="row-dot" aria-hidden />
                        <span className="plans-todo-name">{row.name}</span>
                        <span className={`row-mins mono${met ? ' is-met' : ''}`}>
                          {planned > 0 && logged > 0 ? `${logged}/${planned}m` : fmtMinutes(row.minutes)}
                        </span>
                        <span className="row-chevron" aria-hidden>
                          <ChevronRight size={16} color="currentColor" />
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            </section>
          )}
        </>
      )}

      <div className="plans-add-row">
        <button type="button" className="btn-outline plans-add-btn"
          onClick={() => { haptics.trigger('light'); setAdding(true); }}>
          <Plus size={16} color="currentColor" />Add to plan
        </button>
      </div>

      {editingRow && (
        <Modal title={editingRow.idx === priorityIdx ? 'Edit top priority' : 'Edit activity'}
          onClose={() => setEditingRow(null)}>
          <EditFields row={editingRow.row} onSave={handleEditSave} />
        </Modal>
      )}
      {adding && (
        <Modal title="Add to plan" onClose={() => setAdding(false)}>
          <AddFields onSave={handleAddSave} />
        </Modal>
      )}
      {actionFor && (
        <ActionSheet row={actionFor} onClose={() => setActionFor(null)}
          onDetails={() => {
            const row = actionFor;
            setActionFor(null);
            setDetailFor(row);
          }}
          onEdit={() => {
            const idx = timed.findIndex((r) => r.id === actionFor.id);
            setActionFor(null);
            setEditingRow({ row: idx >= 0 ? timed[idx] : actionFor, idx });
          }}
          onLog={() => {
            const row = actionFor;
            setActionFor(null);
            openLogSheet(row);
          }}
          onRemove={() => {
            if (typeof window !== 'undefined' && !window.confirm('Remove this activity?')) return;
            handleRemove(actionFor);
          }} />
      )}
      <LogTimeSheet
        activity={logSheetFor}
        open={!!logSheetFor}
        onClose={() => setLogSheetFor(null)}
        onLogged={(saved) => handleLogged(logSheetFor, saved)}
        defaultMinutes={logSheetFor?.minutes}
        userId={userId}
      />

      <ActivityDetail
        open={!!detailFor}
        userId={userId}
        activityId={detailFor?.id}
        onClose={() => setDetailFor(null)}
        onDelete={() => {
          // Reuse the Plans-level remove handler so the row disappears
          // and the activity is deleted server-side.
          if (detailFor) {
            const row = detailFor;
            setDetailFor(null);
            handleRemove(row);
          }
        }}
        onSave={(updated) => {
          if (!updated) return;
          setRows((rs) => rs.map((r) => r.id === updated.activity_id || r.id === detailFor?.id
            ? {
                ...r,
                name: updated.name || r.name,
                minutes: updated.duration_minutes || updated.target_minutes || r.minutes,
                time: (updated.preferred_time || r.time || '').slice(0, 5),
              }
            : r));
          setDetailFor(null);
        }}
      />
    </div>
  );
}

// Fix #7: modal a11y — role, aria-modal, aria-labelledby, ESC key, focus management
function Modal({ title, onClose, children }) {
  const firstFocusRef = useRef(null);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  useEffect(() => { firstFocusRef.current?.focus(); }, []);

  return (
    <div className="plans-modal-backdrop" onClick={onClose}>
      <div className="plans-modal" role="dialog" aria-modal="true"
        aria-labelledby="plans-modal-title" onClick={(e) => e.stopPropagation()}>
        <div className="plans-modal-header">
          <h3 id="plans-modal-title" className="h-2">{title}</h3>
          <button type="button" ref={firstFocusRef} className="icon-btn"
            onClick={onClose} aria-label="Close">
            <X size={16} color="currentColor" />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

function EditFields({ row, onSave }) {
  const [name, setName] = useState(row?.name || '');
  const [minutes, setMinutes] = useState(row?.minutes || 30);
  return (
    <>
      <label className="plans-field"><span className="eyebrow">Name</span>
        <input value={name} onChange={(e) => setName(e.target.value)} /></label>
      <label className="plans-field"><span className="eyebrow">Duration (minutes)</span>
        <input type="number" inputMode="numeric" pattern="[0-9]*" value={minutes} onChange={(e) => setMinutes(e.target.value)} /></label>
      <button type="button" className="btn-primary plans-modal-cta"
        onClick={() => onSave({ name, minutes })}>Save</button>
    </>
  );
}

// Fix #5: saving state to disable button while in-flight
function AddFields({ onSave }) {
  const [name, setName] = useState('');
  const [time, setTime] = useState('');
  const [minutes, setMinutes] = useState(30);
  const [saving, setSaving] = useState(false);
  const valid = name.trim();

  const handleSubmit = async () => {
    if (!valid || saving) return;
    setSaving(true);
    try { await onSave({ name: name.trim(), time, minutes }); }
    finally { setSaving(false); }
  };

  return (
    <>
      <label className="plans-field"><span className="eyebrow">Name</span>
        <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Reading block" /></label>
      <div className="plans-field-row">
        <label className="plans-field"><span className="eyebrow">Time (optional)</span>
          <input type="time" value={time} onChange={(e) => setTime(e.target.value)} /></label>
        <label className="plans-field"><span className="eyebrow">Minutes</span>
          <input type="number" inputMode="numeric" pattern="[0-9]*" value={minutes} onChange={(e) => setMinutes(e.target.value)} /></label>
      </div>
      <button type="button" className="btn-primary plans-modal-cta"
        disabled={!valid || saving} onClick={handleSubmit}>
        {saving ? 'Adding…' : 'Add to plan'}
      </button>
    </>
  );
}

// Fix #8: ActionSheet a11y — role, aria-modal, aria-label, ESC key, focus management
// Fix #14: removed Move button
function ActionSheet({ row, onClose, onDetails, onEdit, onLog, onRemove }) {
  const firstFocusRef = useRef(null);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  useEffect(() => { firstFocusRef.current?.focus(); }, []);

  return (
    <div className="plans-modal-backdrop" onClick={onClose}>
      <div className="plans-sheet" role="dialog" aria-modal="true"
        aria-label={row.name} onClick={(e) => e.stopPropagation()}>
        <div className="plans-sheet-handle" aria-hidden />
        <div className="plans-sheet-title">{row.name}</div>
        <button type="button" ref={firstFocusRef} className="plans-sheet-action"
          onClick={onLog}>Log time</button>
        <button type="button" className="plans-sheet-action"
          onClick={onDetails}>View details</button>
        <button type="button" className="plans-sheet-action" onClick={onEdit}>Quick edit</button>
        <button type="button" className="plans-sheet-action plans-sheet-danger"
          onClick={onRemove}>Remove</button>
        <button type="button" className="plans-sheet-cancel" onClick={onClose}>Cancel</button>
      </div>
    </div>
  );
}
